/**
 * Singleton preloader for Bouwbesluit articles.
 * Fetches and caches all articles from Firebase Storage.
 * Similar pattern to faq-preloader.ts.
 */

import { fetchAllArticles } from "@/features/gebreken/services/article-service";
import { ARTICLE_CACHE_KEY, ARTICLE_CACHE_VERSION, CACHE_TTL_MS } from "@/constants";
import type { Article } from "@/features/gebreken/types/article";

interface PreloadedData {
  articles: Article[] | null;
  isLoading: boolean;
  isLoaded: boolean;
  error: Error | null;
}

interface CachedArticleData {
  version: string;
  timestamp: number;
  articles: Article[];
}

class ArticlePreloader {
  private static instance: ArticlePreloader;
  private data: PreloadedData = {
    articles: null,
    isLoading: false,
    isLoaded: false,
    error: null,
  };
  private loadPromise: Promise<void> | null = null;
  private subscribers: Set<() => void> = new Set();

  private constructor() {}

  static getInstance(): ArticlePreloader {
    if (!ArticlePreloader.instance) {
      ArticlePreloader.instance = new ArticlePreloader();
    }
    return ArticlePreloader.instance;
  }

  subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  private notify() {
    this.subscribers.forEach((callback) => callback());
  }

  private loadFromCache(): CachedArticleData | null {
    try {
      const cached = sessionStorage.getItem(ARTICLE_CACHE_KEY);
      if (!cached) return null;

      const data: CachedArticleData = JSON.parse(cached);

      if (data.version !== ARTICLE_CACHE_VERSION) {
        this.clearCache();
        return null;
      }

      return data;
    } catch {
      this.clearCache();
      return null;
    }
  }

  private saveToCache(articles: Article[]): void {
    try {
      const cacheData: CachedArticleData = {
        version: ARTICLE_CACHE_VERSION,
        timestamp: Date.now(),
        articles,
      };
      sessionStorage.setItem(ARTICLE_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn("[ArticlePreloader] Failed to save to cache:", error);
    }
  }

  private isCacheValid(cachedData: CachedArticleData): boolean {
    const age = Date.now() - cachedData.timestamp;
    return age < CACHE_TTL_MS;
  }

  private clearCache(): void {
    try {
      sessionStorage.removeItem(ARTICLE_CACHE_KEY);
    } catch (error) {
      console.warn("[ArticlePreloader] Failed to clear cache:", error);
    }
  }

  async preload(): Promise<void> {
    if (this.data.isLoaded) {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Try loading from cache first
    const cachedData = this.loadFromCache();
    if (cachedData && this.isCacheValid(cachedData)) {
      this.data = {
        articles: cachedData.articles,
        isLoading: false,
        isLoaded: true,
        error: null,
      };
      this.notify();
      return Promise.resolve();
    }

    // Start loading from Firebase Storage
    this.data.isLoading = true;
    this.notify();

    this.loadPromise = this.loadData();
    return this.loadPromise;
  }

  private async loadData(): Promise<void> {
    try {
      const articles = await fetchAllArticles();

      this.data = {
        articles,
        isLoading: false,
        isLoaded: true,
        error: null,
      };

      this.saveToCache(articles);
      this.notify();
    } catch (error) {
      this.clearCache();

      this.data = {
        ...this.data,
        isLoading: false,
        error: error as Error,
      };
      this.notify();
      throw error;
    } finally {
      this.loadPromise = null;
    }
  }

  getData(): PreloadedData {
    return { ...this.data };
  }

  reset(): void {
    this.clearCache();
    this.data = {
      articles: null,
      isLoading: false,
      isLoaded: false,
      error: null,
    };
    this.loadPromise = null;
    this.notify();
  }
}

export const articlePreloader = ArticlePreloader.getInstance();
